import React, { useEffect, useState } from 'react';
import { api } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { TEMPLATE_CATEGORIES } from '../lib/templateUtils';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Copy as CopyIcon, FilePlus2, Loader2, FileText } from 'lucide-react';

const EMPTY = {
  id: null,
  name: '',
  category: 'general_intro',
  body: '',
  language: 'en',
  message_type: 'whatsapp',
  tone: 'polite',
};

const Templates = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/templates');
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(EMPTY); setDialogOpen(true); };
  const openEdit = (tpl) => { setEditing(tpl); setDialogOpen(true); };

  const save = async () => {
    if (!editing.name.trim() || !editing.body.trim()) {
      toast.error(t('templateError'));
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/api/templates/${editing.id}`, {
          name: editing.name,
          category: editing.category,
          body: editing.body,
          language: editing.language,
          message_type: editing.message_type,
          tone: editing.tone,
        });
        toast.success(t('templateUpdated'));
      } else {
        await api.post('/api/templates', editing);
        toast.success(t('templateSaved'));
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(t('templateError'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (tpl) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await api.delete(`/api/templates/${tpl.id}`);
      toast.success(t('templateDeleted'));
      load();
    } catch (e) {
      toast.error(t('templateError'));
    }
  };

  const duplicate = async (tpl) => {
    try {
      await api.post(`/api/templates/${tpl.id}/duplicate`);
      toast.success(t('templateDuplicated'));
      load();
    } catch (e) {
      toast.error(t('templateError'));
    }
  };

  const copy = async (tpl) => {
    await navigator.clipboard.writeText(tpl.body);
    toast.success(t('copied'));
  };

  const filtered = activeCategory === 'all' ? items : items.filter((i) => i.category === activeCategory);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="templates-page-title">{t('templatesPageTitle')}</h1>
            <p className="text-sm text-slate-500 mt-1">{t('templatesPageSubtitle')}</p>
          </div>
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="new-template-button">
            <Plus className="w-4 h-4 mr-2" />{t('newTemplate')}
          </Button>
        </div>

        {/* Category filters */}
        <div className="flex gap-1.5 overflow-x-auto mb-4" data-testid="templates-category-bar">
          <button
            type="button"
            onClick={() => setActiveCategory('all')}
            className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors ${
              activeCategory === 'all'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
            data-testid="templates-cat-all"
          >
            {t('allCategories')}
          </button>
          {TEMPLATE_CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveCategory(cat)}
              className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                activeCategory === cat
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid={`templates-cat-${cat}`}
            >
              {t(`cat_${cat}`)}
            </button>
          ))}
        </div>

        {/* List */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="templates-empty">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-medium">{t('noTemplates')}</p>
            <p className="text-sm text-slate-500 mt-1">{t('noTemplatesDesc')}</p>
            <Button onClick={openCreate} className="mt-4 bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />{t('newTemplate')}
            </Button>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="templates-grid">
            {filtered.map((tpl) => (
              <div key={tpl.id} className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col" data-testid="template-card">
                <div className="flex items-start gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{tpl.name}</h3>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                        {t(`cat_${tpl.category}`)}
                      </span>
                      <span className="text-[10px] uppercase bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">
                        {tpl.message_type}
                      </span>
                      {tpl.is_default ? (
                        <span className="text-[10px] uppercase bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{t('isDefault')}</span>
                      ) : (
                        <span className="text-[10px] uppercase bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">{t('customLabel')}</span>
                      )}
                      {tpl.usage_count > 0 && (
                        <span className="text-[10px] text-slate-400">{tpl.usage_count} {t('usageCount')}</span>
                      )}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-600 line-clamp-3 whitespace-pre-line flex-1 mb-3">{tpl.body}</p>
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => copy(tpl)} data-testid="template-copy-btn">
                    <CopyIcon className="w-3.5 h-3.5 mr-1" />{t('copyToClipboard')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(tpl)} data-testid="template-edit-btn">
                    <Edit2 className="w-3.5 h-3.5 mr-1" />{t('edit')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => duplicate(tpl)} data-testid="template-duplicate-btn">
                    <FilePlus2 className="w-3.5 h-3.5 mr-1" />{t('duplicate')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(tpl)} className="text-rose-600 hover:bg-rose-50" data-testid="template-delete-btn">
                    <Trash2 className="w-3.5 h-3.5 mr-1" />{t('delete')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="sm:max-w-lg p-0 gap-0 h-screen max-h-screen w-screen rounded-none inset-0 translate-x-0 translate-y-0 sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:h-auto sm:max-h-[90vh] sm:w-auto sm:rounded-lg overflow-hidden flex flex-col"
          data-testid="template-edit-dialog"
        >
          <DialogHeader className="px-4 py-3 sm:px-6 sm:py-4 border-b border-slate-200">
            <DialogTitle>{editing.id ? t('edit') : t('newTemplate')}</DialogTitle>
            <DialogDescription className="sr-only">{editing.id ? t('edit') : t('newTemplate')}</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500">{t('templateName')}</Label>
              <Input
                value={editing.name}
                onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
                placeholder={t('templateNamePlaceholder')}
                data-testid="template-name-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500">{t('templateCategory')}</Label>
              <select
                value={editing.category}
                onChange={(e) => setEditing((s) => ({ ...s, category: e.target.value }))}
                className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="template-category-select"
              >
                {TEMPLATE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{t(`cat_${c}`)}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500">{t('messageType')}</Label>
                <select
                  value={editing.message_type}
                  onChange={(e) => setEditing((s) => ({ ...s, message_type: e.target.value }))}
                  className="w-full h-9 px-2 border border-slate-200 rounded-md text-sm bg-white"
                >
                  <option value="whatsapp">{t('msgTypeWhatsapp')}</option>
                  <option value="email">{t('msgTypeEmail')}</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500">{t('messageTone')}</Label>
                <select
                  value={editing.tone}
                  onChange={(e) => setEditing((s) => ({ ...s, tone: e.target.value }))}
                  className="w-full h-9 px-2 border border-slate-200 rounded-md text-sm bg-white"
                >
                  <option value="polite">{t('toneePolite')}</option>
                  <option value="direct">{t('toneDirect')}</option>
                  <option value="negotiation">{t('toneNegotiation')}</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500">{t('messageLanguage')}</Label>
                <select
                  value={editing.language}
                  onChange={(e) => setEditing((s) => ({ ...s, language: e.target.value }))}
                  className="w-full h-9 px-2 border border-slate-200 rounded-md text-sm bg-white"
                >
                  <option value="en">{t('english')}</option>
                  <option value="fr">{t('french')}</option>
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500">{t('templateBody')}</Label>
              <Textarea
                value={editing.body}
                onChange={(e) => setEditing((s) => ({ ...s, body: e.target.value }))}
                className="min-h-[180px] font-mono text-sm"
                data-testid="template-body-input"
              />
              <p className="text-[10px] text-slate-500">{t('templateBodyHelp')}</p>
            </div>
          </div>

          <div className="border-t border-slate-200 p-4 sm:p-6 bg-white flex gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">{t('cancel')}</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              data-testid="template-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {t('save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Templates;
